import { axiosBaseQuery } from '@app/api/make-request'
import { ApiKeys } from '@app/constants/api-keys'
import { createApi } from '@reduxjs/toolkit/query/react'
import localforage from 'localforage'
import { IRtkResponse } from '../types'
import {
  IAllReferenceBillingTariffHistory,
  IBulkCreateReferenceBillingTariffBody,
  ICreateReferenceBillingTariffBody,
  IReferenceBillingTariff,
  IReferenceBillingTariffHistory,
  IUpdateReferenceBillingTariff,
} from '@app/interfaces/reference-billing'

export const billingTariffsApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'billingTariffsApi',
  tagTypes: [ApiKeys.billingTariff],
  endpoints: (build) => ({
    getBillingTariffs: build.query<
      IRtkResponse<IReferenceBillingTariff[]>,
      void
    >({
      query: () => ({
        url: 'dictionary/api/v1/organization-payment-prices',
        method: 'get',
      }),
      transformResponse: (response: IReferenceBillingTariff[], meta) => {
        return { response, meta }
      },
      providesTags: (data) =>
        data
          ? [
              ...data.response.map(({ id }) => ({
                type: ApiKeys.billingTariff,
                id,
              })),
              { type: ApiKeys.billingTariff, id: 'LIST' },
            ]
          : [{ type: ApiKeys.billingTariff, id: 'LIST' }],
      async onQueryStarted(args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          localforage.setItem(ApiKeys.billingTariff, data?.response)
        } catch (e) {
          console.error(e)
        }
      },
    }),
    createBillingTariff: build.mutation<
      IRtkResponse<IReferenceBillingTariff>,
      ICreateReferenceBillingTariffBody
    >({
      query: (data) => ({
        method: 'POST',
        url: 'dictionary/api/v1/organization-payment-prices',
        data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: ApiKeys.billingTariff, id: 'LIST' },
      ],
    }),
    updateBillingTariff: build.mutation<
      IRtkResponse<IReferenceBillingTariff>,
      IUpdateReferenceBillingTariff
    >({
      query: (data) => ({
        method: 'PUT',
        url: `dictionary/api/v1/organization-payment-prices/${data.id}`,
        data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: ApiKeys.billingTariff, id: 'LIST' },
        { type: ApiKeys.billingTariff, id: arg.id },
      ],
    }),

    getAllBillingTariffHistory: build.query<
      IRtkResponse<IAllReferenceBillingTariffHistory[]>,
      {
        page: number
        size: number
      }
    >({
      query: ({ page, size }) => ({
        url: `dictionary/api/v1/organization-payment-prices/histories`,
        method: 'get',
        params: { page, size },
      }),
      transformResponse: (
        response: IAllReferenceBillingTariffHistory[],
        meta,
      ) => {
        return {
          response,
          meta: { totalCount: meta?.['x-total-count'] },
        }
      },
    }),
    getOneBillingTariffHistory: build.query<
      IRtkResponse<IReferenceBillingTariffHistory[]>,
      number
    >({
      query: (tariffId) => ({
        url: `dictionary/api/v1/organization-payment-prices/histories/${tariffId}`,
        method: 'get',
      }),
      transformResponse: (response: IReferenceBillingTariffHistory[], meta) => {
        return { response, meta }
      },
    }),
    addOrUpdateTariffGroupData: build.mutation<
      void,
      IBulkCreateReferenceBillingTariffBody
    >({
      query: (body) => ({
        url: `dictionary/api/v1/organization-payment-prices/bulk`,
        method: 'POST',
        data: body,
        suppressNotification: true,
      }),
      invalidatesTags: (result, error, arg) =>
        result ? [{ type: ApiKeys.billingTariff, id: 'LIST' }] : [],
      // async onQueryStarted(body, { dispatch, queryFulfilled }) {
      //   try {
      //     await queryFulfilled
      //     dispatch(
      //       billingTariffsApi.util.updateQueryData(
      //         'getBillingTariffs',
      //         undefined,
      //         (draft) => {
      //           body.schedulePrices.forEach((schedule) => {
      //             draft.response.forEach((tariff) => {
      //               if (
      //                 tariff.organizationLocality.id === body.localityId &&
      //                 tariff.organizationSchedule.id === schedule.scheduleId
      //               ) {
      //                 tariff.value = schedule.value
      //                 tariff.discountedValue = schedule.discountedValue
      //                 tariff.orderNo = body.orderNo
      //                 tariff.orderDate = body.orderDate
      //               }
      //             })
      //           })
      //         },
      //       ),
      //     )
      //   } catch (e) {
      //     console.error(e)
      //   }
      // },
    }),
    updateOneTariffData: build.mutation<void, IUpdateReferenceBillingTariff>({
      query: (body) => ({
        url: `dictionary/api/v1/organization-payment-prices/${body.id}`,
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: (result, error, arg) =>
        result ? [{ type: ApiKeys.billingTariff, id: 'LIST' }] : [],
      // async onQueryStarted(body, { dispatch, queryFulfilled }) {
      //   try {
      //     await queryFulfilled
      //     dispatch(
      //       billingTariffsApi.util.updateQueryData(
      //         'getBillingTariffs',
      //         undefined,
      //         (draft) => {
      //           draft.response.forEach((tariff) => {
      //             if (tariff.id === body.id) {
      //               tariff.value = body.value
      //               tariff.discountedValue = body.discountedValue
      //               tariff.orderNo = body.orderNo
      //               tariff.orderDate = body.orderDate
      //             }
      //           })
      //         },
      //       ),
      //     )
      //   } catch (e) {
      //     console.error(e)
      //   }
      // },
    }),
    updateTariffStatus: build.mutation<void, { id: number; status: boolean }>({
      query: (body) => ({
        url: `dictionary/api/v1/organization-payment-prices/${body.id}`,
        method: 'PATCH',
        data: body,
        type: 'patch',
      }),
      invalidatesTags: (result) =>
        result ? [{ type: ApiKeys.billingTariff, id: 'LIST' }] : [],
      // async onQueryStarted(body, { dispatch, queryFulfilled }) {
      //   try {
      //     await queryFulfilled
      //     dispatch(
      //       billingTariffsApi.util.updateQueryData(
      //         'getBillingTariffs',
      //         undefined,
      //         (draft) => {
      //           draft.response.forEach((tariff) => {
      //             if (tariff.id === body.id) {
      //               tariff.status = body.status
      //             }
      //           })
      //         },
      //       ),
      //     )
      //   } catch (e) {
      //     console.error(e)
      //   }
      // },
    }),
  }),
})

const useStateGetBillingTariffs =
  billingTariffsApi.endpoints.getBillingTariffs.useQueryState

export const {
  useGetBillingTariffsQuery,
  useGetOneBillingTariffHistoryQuery,
  useGetAllBillingTariffHistoryQuery,
  useUpdateTariffStatusMutation,
  useAddOrUpdateTariffGroupDataMutation,
  useUpdateOneTariffDataMutation,
} = billingTariffsApi

export { useStateGetBillingTariffs }
